import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';

import { NgxFileDropEntry, NgxFileDropModule } from 'ngx-file-drop';
import { catchError, forkJoin, Observable, of } from 'rxjs';

import { FileUploadService } from '../../services/file-upload.service';
import { NotificationService } from '../../services/notification.service';

@Component({
  selector: 'app-filedrop',
  standalone: true,
  imports: [NgxFileDropModule],
  templateUrl: './filedrop.component.html',
  styleUrl: './filedrop.component.scss',
})
export class FiledropComponent implements OnChanges {
  // Bereits gespeicherte Anhänge als MinIO-Object-Keys, keine abrufbaren URLs
  @Input() oldFiles: string[] = [];

  @Input() bucketname: string = '';

  files: File[] = [];
  isUploading: boolean = false;

  // Entfernte Anhänge werden nur vorgemerkt und erst in requestUpload() gelöscht.
  // So kostet ein abgebrochener Dialog keine Datei.
  private removedKeys: string[] = [];

  @Output() fileUploadCompleted = new EventEmitter<string[]>();
  @Output() fileUploadStarted = new EventEmitter<boolean>();

  constructor(
    private notificationService: NotificationService,
    private fileUploadService: FileUploadService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['oldFiles']) return;

    // Bewusst eine Kopie: ohne sie würde removeOldFile die Liste des Elternteils
    // (z.B. activity.consultation.fileUrls) direkt verändern.
    this.oldFiles = [...(this.oldFiles ?? [])];
    this.removedKeys = [];
  }

  requestUpload(): void {
    this.deleteRemovedFiles().subscribe(() => {
      if (this.files.length === 0) {
        this.fileUploadCompleted.emit([...this.oldFiles]);
        return;
      }
      this.uploadNewFiles();
    });
  }

  private uploadNewFiles(): void {
    this.isUploading = true;
    this.fileUploadStarted.emit(true);

    this.fileUploadService
      .uploadToMinio(this.files, this.bucketname)
      .subscribe({
        next: (uploadedKeys) => {
          const allKeys = [...uploadedKeys, ...this.oldFiles];
          this.fileUploadCompleted.emit(allKeys);
          this.notificationService.success(`Dateien hochgeladen!`);
          this.files = [];
          this.isUploading = false;
        },
        error: () => {
          this.fileUploadCompleted.emit([]);
          this.notificationService.error(`Dateien NICHT hochgeladen!`);
          this.isUploading = false;
        },
      });
  }

  // Eine fehlgeschlagene Löschung darf das Speichern nicht blockieren - das Objekt
  // bleibt dann verwaist im Bucket zurück, die Referenz verschwindet trotzdem.
  private deleteRemovedFiles(): Observable<unknown> {
    const pending = this.removedKeys;
    if (pending.length === 0) return of(null);

    // Vor dem Absenden leeren, damit ein zweiter Submit nicht erneut löscht
    this.removedKeys = [];

    return forkJoin(
      pending.map((objectKey) =>
        this.fileUploadService.deleteFile(objectKey).pipe(
          catchError(() => {
            this.notificationService.warn(
              `Datei konnte nicht gelöscht werden: ${objectKey}`,
            );
            return of(null);
          }),
        ),
      ),
    );
  }

  onFileDropped(droppedfiles: NgxFileDropEntry[]): void {
    droppedfiles.forEach((droppedFile) => {
      if (droppedFile.fileEntry.isFile) {
        const fileEntry = droppedFile.fileEntry as FileSystemFileEntry;
        fileEntry.file((file: File) => {
          this.files.push(file);
        });
      }
    });
  }

  extractFilename(objectKey: string): string {
    return objectKey.substring(objectKey.lastIndexOf('/') + 1);
  }

  fileBrowseHandler(event: any): void {
    const selectedFiles: FileList = event.target.files;
    for (let i = 0; i < selectedFiles.length; i++) {
      const file = selectedFiles[i];
      this.files.push(file);
    }
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
  }

  removeOldFile(index: number): void {
    const [removedKey] = this.oldFiles.splice(index, 1);
    if (removedKey) {
      this.removedKeys.push(removedKey);
    }
  }
}
